import { useState, useEffect, Suspense } from 'react';
import { useLateralAutomatica } from '@/hooks/useLateralAutomatica';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { DevClubLogo } from '@/components/ui/DevClubLogo';
import {
  Settings,
  LogOut,
  Menu,
  X,
  Pin,
  PinOff,
  ArrowLeft,
  Sun,
  Moon,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { GamifiedAvatar } from '@/components/ui/GamifiedAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AppToolTip } from '../ui/AppToolTip';
import { CarregandoPagina } from '@/components/ui/CarregandoPagina';
import { useRealceDoMenu } from './useRealceDoMenu';
import { canOpenAdminPath } from '@/components/guards/AdminRoute';
import { useInboxSummary } from '@/hooks/useCommentInbox';
import { usePlatformTheme } from '@/hooks/usePlatformTheme';
import './admin.css';
import { adminNavigation } from '@/lib/admin-navigation';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  exact?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}


export function AdminLayout() {
  const { theme, toggleTheme } = usePlatformTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  /**
   * No notebook a barra é uma faixa de ícones que abre sozinha por cima do
   * conteúdo quando o mouse entra e recolhe quando ele sai; o botão do topo
   * fixa ela aberta (a escolha fica salva).
   */
  const { fixada, alternarFixada, sobre, eventos } = useLateralAutomatica(
    'admin-sidebar-fixada',
  );
  const aberta = fixada || sobre;
  const location = useLocation();
  const { user, logout } = useAuthStore();
  // Instrutor vê só o que pode abrir (a caixa de dúvidas); grupo vazio some.
  const groups = adminNavigation(import.meta.env.VITE_HOME_URL)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canOpenAdminPath(user?.role, item.href)),
    }))
    .filter((group) => group.items.length > 0);
  const { data: inbox } = useInboxSummary(!!user);
  const counters: Partial<Record<string, number>> = {
    '/admin/duvidas': inbox?.pending,
  };

  // Helper: check if a nav item matches the current route
  const isActive = (item: NavItem) => {
    if (item.exact) {
      return location.pathname === item.href;
    }
    return location.pathname.startsWith(item.href);
  };

  // Helper: check if any item in a group matches the current route
  const isGroupActive = (group: NavGroup) => {
    return group.items.some((item) => isActive(item));
  };

  // o realce da lateral desliza com mola e se arrasta (como as abas do painel)
  const itemAtivo =
    groups.flatMap((g) => g.items).find(isActive)?.href ?? null;
  const menu = useRealceDoMenu(itemAtivo, 'admin-nav-selection');

  // Find the current page name for the breadcrumb
  const currentPageName = (() => {
    for (const group of groups) {
      for (const item of group.items) {
        if (
          item.exact
            ? location.pathname === item.href
            : location.pathname.startsWith(item.href)
        ) {
          // Don't show breadcrumb segment for the dashboard root
          if (item.exact && item.href === '/admin') return null;
          return item.name;
        }
      }
    }
    return null;
  })();

  // O escopo no body inclui os portais e é removido ao voltar à área do aluno.
  useEffect(() => {
    const previousTheme = document.body.dataset.adminTheme;
    document.body.dataset.adminTheme = 'devclub-glass';
    return () => {
      if (previousTheme === undefined) delete document.body.dataset.adminTheme;
      else document.body.dataset.adminTheme = previousTheme;
    };
  }, []);

  return (
    <div className="admin-shell min-h-screen overflow-x-hidden">
      <a className="admin-skip-link" href="#admin-content">Pular para o conteúdo</a>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="animate-fade-in fixed inset-0 z-40 bg-[var(--overlay-scrim)] backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        id="admin-navigation"
        data-lateral=""
        data-aberta={aberta}
        data-mobile-open={sidebarOpen}
        {...eventos}
        className={cn(
          'admin-glass-pane admin-floating-sidebar fixed z-50 flex w-[260px] transform flex-col overflow-hidden transition-[width,transform] duration-200',
          // Mobile: slide in/out
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          // Desktop: faixa de 72px que abre para 260px (fixa ou no hover)
          aberta ? 'lg:w-[260px]' : 'lg:w-[72px]',
          // aberta por cima do conteúdo (no hover), ganha sombra para descolar
          !fixada && sobre && 'lg:shadow-2xl lg:shadow-black/50',
        )}
      >
        {/* Largura fixa por dentro: a barra cresce e os textos já estão no
            lugar, sem quebrar linha enquanto ela abre. */}
        <div className="flex h-full w-full flex-col lg:w-[260px] lg:shrink-0">
          {/* Sidebar Header */}
          <div className="admin-sidebar-heading flex shrink-0 items-center justify-between gap-2 pl-[22px] pr-2">
            <Link
              to="/admin"
              className="rotulo-logo flex min-w-0 items-center gap-2"
            >
              <DevClubLogo size="sm" showText />
            </Link>
            {/* Mobile close */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              title="Fechar menu"
            >
              <X className="h-5 w-5" />
            </Button>
            {/* Desktop: fixa a barra aberta, ou deixa ela abrir sozinha no hover */}
            <AppToolTip
              content={
                fixada ? 'Deixar o menu abrir sozinho' : 'Fixar o menu aberto'
              }
            >
              <Button
                variant="ghost"
                size="icon-sm"
                className="rotulo hidden lg:inline-flex"
                onClick={alternarFixada}
                aria-pressed={fixada}
                aria-label={
                  fixada ? 'Deixar o menu abrir sozinho' : 'Fixar o menu aberto'
                }
              >
                {fixada ? (
                  <PinOff className="text-muted-foreground hover:text-foreground h-4 w-4 transition-colors" />
                ) : (
                  <Pin className="text-muted-foreground hover:text-foreground h-4 w-4 transition-colors" />
                )}
              </Button>
            </AppToolTip>
          </div>

          {/* Navigation - scrollable area */}
          <nav
            ref={menu.navRef}
            aria-label="Administração"
            {...menu.navProps}
            className={cn(
              'relative flex-1 select-none overflow-y-auto overflow-x-hidden px-3 py-3',
              menu.arrastando && 'cursor-grabbing',
            )}
          >
            {menu.realce}
            {groups.map((group) => {
              const groupActive = isGroupActive(group);

              return (
                <div key={group.label} className="admin-menu-group">
                  {/* Group Header: na faixa fica invisível, mas guarda o
                        lugar, para os itens não pularem quando a barra abre */}
                  <div
                    className={cn(
                      'admin-menu-group-label rotulo flex w-full items-center px-4 py-1.5 text-xs font-medium',
                      groupActive
                        ? 'text-foreground'
                        : 'text-muted-foreground',
                    )}
                  >
                    <span className="truncate">{group.label}</span>
                  </div>

                  {/* Group Items */}
                  <div className="mt-0.5 space-y-0.5">
                    {group.items.map((item) => {
                      const active = menu.aceso === item.href;
                      const counter = counters[item.href];
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          data-item-menu={item.href}
                          draggable={false}
                          onClick={() => setSidebarOpen(false)}
                          aria-current={active ? 'page' : undefined}
                          className={cn(
                            'admin-menu-link group relative flex items-center gap-2.5 rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200',
                            active
                              ? 'text-primary'
                              : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                          )}
                        >
                          <item.icon
                            className={cn(
                              'relative h-4 w-4 shrink-0 transition-colors',
                              active
                                ? 'text-primary'
                                : 'group-hover:text-foreground',
                            )}
                          />
                          <span className="rotulo relative flex-1 truncate">
                            {item.name}
                          </span>
                          {!!counter && (
                            <span
                              className="rotulo bg-primary text-primary-foreground relative rounded-full px-1.5 text-[10px] font-semibold leading-4"
                              aria-label={`${counter} pendentes`}
                            >
                              {counter > 99 ? '99+' : counter}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          {/* Bottom Actions */}
          <div className="admin-sidebar-actions shrink-0 space-y-0.5 p-3">
            <Link
              to="/home"
              onClick={() => setSidebarOpen(false)}
              className="admin-menu-link text-muted-foreground hover:text-primary hover:bg-primary/5 group flex items-center gap-2.5 rounded-md px-4 py-2 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="h-4 w-4 shrink-0" />
              <span className="rotulo truncate">Voltar ao App</span>
            </Link>
            <button
              onClick={logout}
              className="admin-menu-link text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex w-full items-center gap-2.5 rounded-md px-4 py-2 text-sm font-medium transition-colors"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="rotulo">Sair</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={cn(
          'admin-main-workspace flex min-h-screen flex-col overflow-x-hidden transition-[padding] duration-200',
          fixada ? 'lg:pl-[284px]' : 'lg:pl-[96px]',
        )}
      >
        {/* Header */}
        <header className="admin-glass-pane admin-floating-topbar sticky z-30">
          <div className="flex h-full items-center justify-between px-3 sm:px-4 lg:px-6">
            <div className="flex items-center gap-3">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                title="Abrir menu"
                aria-expanded={sidebarOpen}
                aria-controls="admin-navigation"
              >
                <Menu className="h-5 w-5" />
              </Button>
              {/* Breadcrumb-style page indicator */}
              <div className="hidden items-center gap-2 sm:flex">
                <span className="text-muted-foreground text-sm">
                  Administração
                </span>
                {currentPageName && (
                  <>
                    <span className="text-muted-foreground/40 text-sm">/</span>
                    <span className="text-foreground text-sm font-medium">
                      {currentPageName}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
              </Button>
              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    aria-label="Abrir menu da conta"
                  >
                    <GamifiedAvatar
                      src={user?.avatar}
                      name={user?.name}
                      size="sm"
                    />
                    <div className="hidden text-left sm:block">
                      <p className="text-sm font-medium leading-none">
                        {user?.name}
                      </p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="admin-glass-pane text-foreground w-56"
                >
                  <div className="px-3 py-2">
                    <p className="font-semibold">{user?.name}</p>
                    <p className="text-muted-foreground text-xs">
                      {user?.email}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Voltar ao App
                    </Link>
                  </DropdownMenuItem>
                  {canOpenAdminPath(user?.role, '/admin/settings') && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/settings" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        Configurações
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="text-destructive focus:text-destructive cursor-pointer"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="admin-content" className="admin-content flex-1" tabIndex={-1}>
          <div>
            <Suspense fallback={<CarregandoPagina />}>
              <Outlet />
            </Suspense>
          </div>
        </main>

        {/* Footer */}
        <footer className="admin-platform-footer px-3 py-3 sm:px-4 lg:px-6">
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              &copy; {new Date().getFullYear()} GrupoClub
              <span className="text-muted-foreground/40">·</span>
              <span className="text-primary flex items-center gap-1 font-medium">
                <DevClubLogo size="sm" showText={false} />
                DevClub
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
